import React from "react";
import moment from "moment";
import Button from "./Button";
import Heading from "./Heading";
import RemoteIcon from "./RemoteIcon";
import { Fragment } from "react";
import { useState } from "react";
import ApiServices from "../api/services";
import { useLocation, useParams } from "react-router-dom";
import { images } from "../assets/styles/Images";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useLoggedContext } from "../context/provider";
import "react-image-gallery/styles/css/image-gallery.css";
import { Dialog, RadioGroup, Transition } from "@headlessui/react";

const MentalHealthAbsenceModal = ({ isOpen, setIsOpen, data, refreshData }) => {
  // react router hook
  const location = useLocation();
  const { id: paramId } = useParams();
  const { employeeDetail } = location.state || {};
  const employeeId = employeeDetail ? employeeDetail?.id : null;

  // Context
  const { btnLoading, setBtnLoading, openSuccessModal, feelingsByPolarity, reasonsForFeeling } = useLoggedContext();

  // Dynamic, API-driven data. The absence modal historically shows only the
  // unhealthy feelings (Meh/Low/Struggling); filtering by polarity keeps that
  // behaviour while auto-including any future unhealthy feelings.
  const feelingData = feelingsByPolarity("unhealthy");

  // useState
  const [selectedReason, setSelectedReason] = useState(null); // reason object
  const [reasonDetail, setReasonDetail] = useState("");
  const [message, setMessage] = useState("");

  const [selectedFeeling, setSelectedFeeling] = useState(null); // feeling object

  // Only the chosen feeling's own reasons are offered (API rejects mismatches with 400).
  const availableReasons = reasonsForFeeling(selectedFeeling);

  // handle reason detail
  const handleReasonChange = (event) => {
    setReasonDetail(event.target.value);
  };

  // When the feeling changes, clear a now-invalid reason selection.
  const handleFeelingChange = (feeling) => {
    setSelectedFeeling(feeling);
    setSelectedReason(null);
  };

  // Close Modal
  function closeModal() {
    setIsOpen(false);
    setSelectedReason(null);
    setReasonDetail("");
    setMessage("");
    setSelectedFeeling(null);
  }

  // handle submit response
  const submitResponse = () => {
    if (!selectedFeeling) {
      setMessage("A feeling is required");
      return;
    } else {
      setBtnLoading(true);
      const formData = new FormData();
      formData.append("id", employeeId ? employeeId : paramId);
      formData.append("feeling_id", selectedFeeling.id);
      if (selectedReason) {
        formData.append("reason_id", selectedReason.id);
      }
      formData.append("reason_detail", reasonDetail);
      formData.append("is_admin", 1);
      formData.append("mark_absence", 1);

      ApiServices.employees
        .markMentalHealthAbsenceByID(formData)
        .then((res) => {
          if (res?.status === 200) {
            setBtnLoading(false);
            setMessage("");
            refreshData();
            openSuccessModal({
              title: "Success!",
              message: "Response has been Sent!",
              onPress: (close) => {
                close();
              },
            });
            setSelectedReason(null);
            setReasonDetail("");
            setSelectedFeeling(null);
            setIsOpen(false);
          }
        })
        .catch(({ response }) => {
          setBtnLoading(false);
          // Surface API validation errors (e.g. 400 reason-not-linked-to-feeling).
          setMessage(response?.data?.message || "Something went wrong. Please try again.");
        });
    }
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-50"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-50"
              >
                <Dialog.Panel className="scal  w-full max-w-[88rem] transform space-y-4 overflow-hidden  rounded-2xl bg-white p-10 align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="font-poppins text-lg font-semibold leading-7 text-secondary-color">
                      {data}
                    </Dialog.Title>
                    <XMarkIcon onClick={closeModal} className="h-8 w-8 cursor-pointer text-grayColor300" />
                  </div>
                  <div className="-mr-4 h-full max-h-[80vh] overflow-y-auto pr-4">
                    <div className="grid grid-cols-12 items-start gap-10">
                      <div className="col-span-8 space-y-4">
                        <div className="">
                          <div>
                            <Heading heading="What’s Feeling?" />
                            <RadioGroup value={selectedFeeling} onChange={handleFeelingChange}>
                              <div className="grid grid-cols-3 gap-6">
                                {feelingData.map((feeling) => (
                                  <RadioGroup.Option key={feeling.id} value={feeling}>
                                    {({ checked }) => (
                                      <div
                                        className={`flex cursor-pointer  items-center justify-center rounded-md bg-gray-100 pt-4 pb-5 ${checked && "border border-secondary"}`}
                                      >
                                        <div className="space-y-5">
                                          <RemoteIcon src={feeling.icon_url} alt={feeling.name} className="mx-auto h-8 w-8" />
                                          <p className="text-sm text-secondary-color">{feeling.name}</p>
                                        </div>
                                      </div>
                                    )}
                                  </RadioGroup.Option>
                                ))}
                              </div>
                            </RadioGroup>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <Heading heading="What’s Reason?" />
                          </div>
                          <div>
                            {!selectedFeeling ? (
                              <p className="text-sm text-grayColor300">Select a feeling first to see its reasons.</p>
                            ) : availableReasons.length === 0 ? (
                              <p className="text-sm text-grayColor300">No reasons available for this feeling.</p>
                            ) : (
                              <RadioGroup value={selectedReason} onChange={setSelectedReason}>
                                <div className="grid grid-cols-4 2xl:grid-cols-5 gap-4">
                                  {availableReasons.map((reason) => (
                                    <RadioGroup.Option key={reason.id} value={reason}>
                                      {({ checked }) => (
                                        <div
                                          className={`flex cursor-pointer  items-center justify-center rounded-md bg-gray-100 pt-4 pb-5 ${checked && "border border-secondary"}`}
                                        >
                                          <div className="space-y-3">
                                            <RemoteIcon src={reason.icon_url} alt={reason.name} className="mx-auto h-9 w-9" />
                                            <p className="text-[12px] text-secondary-color">{reason.name}</p>
                                          </div>
                                        </div>
                                      )}
                                    </RadioGroup.Option>
                                  ))}
                                </div>
                              </RadioGroup>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-4   flex h-full flex-col items-center justify-center">
                        <div className="w-full flex-grow">
                        
                          <Heading heading="Additional Detail" />
                          <textarea
                            name="reason"
                            id="reason"
                            rows="20"
                            className="border-1 w-full rounded-lg border-grayColor200 p-4 text-sm placeholder:text-primary-color-100"
                            placeholder="Write here ...."
                            value={reasonDetail}
                            onChange={handleReasonChange}
                          />
                        </div>
                        <div className="w-full">
                          <div className="flex justify-center">
                            <p className="text-sm text-red-500">{message}</p>
                          </div>
                          <div className="mt-20 flex w-full flex-col items-center gap-4">
                            <Button
                              loading={btnLoading}
                              title="Mark as Mental Health Rest Day"
                              buttonColor="bg-red-500 text-white "
                              className="w-full"
                              onClick={submitResponse}
                            />
                            <Button
                              onClick={closeModal}
                              title="Cancel"
                              buttonColor="border border-red-500 hover:bg-red-500 hover:text-white text-red-500"
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default MentalHealthAbsenceModal;
